// P12 — TSS (Tradeatory Strategy Script) authoring view (Phase C).
// TSS is what users/AI write; it compiles to TSL (Tradeatory Strategy
// Language), the canonical graph IR — and the engine executes TSL only.
//
// THE CONTRACT (divergence guarantee, see strategyScript.ts):
//   • The script shown here is ALWAYS printGraph(currentBlocks) — a
//     projection of the live graph, never stored text loaded blindly.
//   • DEFAULT VIEW = FULL CODE (canonical block bodies inline) so the user
//     sees the entire logic; a second "Compact" view folds canonical
//     blocks to `use id(params)` references. Both compile identically.
//   • Applying compiles script → canonical TSL and replaces the graph
//     through the SAME replace flow imports use.
//   • If a stored sourceText exists but its sourceHash no longer matches
//     the current graph, a "regenerated from graph" badge appears.
//   • The script never executes. The engine runs the compiled TSL only.
//
// Editor: dependency-free overlay technique — a syntax-highlighted <pre>
// behind a transparent-text <textarea>, scroll-synced, with a line-number
// gutter and a Ln/Col status bar.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Code2, X, Wand2, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import type { Block } from '../../types';
import {
  compileScript, printGraph, graphSemanticHash,
  CANONICAL_BLOCKS, type ScriptDiagnostic,
} from '../../../supabase/functions/_shared/strategyScript';

interface ScriptViewProps {
  blocks: Block[];
  onApply: (blocks: Block[]) => void;
}

// ── Lightweight TSS syntax highlighter (no deps) ───────────────────────────
// One combined alternation; segments between matches render as plain text.
// Order matters: comments > strings > keywords > numbers.
const HL_RE = new RegExp(
  [
    '(\\/\\/[^\\n]*)',                                              // 1 comment
    '("(?:\\\\.|[^"\\\\\\n])*")',                                   // 2 string
    "('(?:\\\\.|[^'\\\\\\n])*')",                                   // 3 string'
    '(\\b(?:strategy|ind|cond|pattern|filter|calc|risk|entry|stop|take|raw|use|tsl|ts|when|and|or|not|long|short|qty|sl|tp)\\b)', // 4 tss keyword
    '(\\b(?:function|return|const|let|var|if|else|for|while|continue|break|new|typeof|true|false|null|undefined)\\b)',        // 5 code keyword
    '(\\b\\d+(?:\\.\\d+)?%?\\b)',                                   // 6 number
  ].join('|'),
  'g',
);
const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function highlight(src: string): string {
  let out = '';
  let last = 0;
  for (const m of src.matchAll(HL_RE)) {
    out += esc(src.slice(last, m.index));
    const [whole, comment, str1, str2, kw, codeKw, num] = m;
    if (comment) out += `<span class="text-gray-400 italic">${esc(whole)}</span>`;
    else if (str1 || str2) out += `<span class="text-emerald-700">${esc(whole)}</span>`;
    else if (kw) out += `<span class="text-indigo-700 font-semibold">${esc(whole)}</span>`;
    else if (codeKw) out += `<span class="text-sky-700">${esc(whole)}</span>`;
    else if (num) out += `<span class="text-amber-700">${esc(whole)}</span>`;
    else out += esc(whole);
    last = (m.index ?? 0) + whole.length;
  }
  out += esc(src.slice(last));
  return out;
}

// Shared font metrics between gutter / highlight layer / textarea — they
// MUST be identical or the overlay drifts.
const MONO = 'font-mono text-[12.5px] leading-5';

const SNIPPETS: Array<{ label: string; text: string }> = [
  { label: 'indicator', text: 'ind ema200 = moving_average(indicator: ema, period: 200, source: close)' },
  { label: 'oscillator', text: 'ind rsi14 = momentum_oscillator(indicator: rsi, period: 14, overbought: 70, oversold: 30)' },
  { label: 'condition', text: 'cond bull = close > ema200' },
  { label: 'session', text: 'ind sess = time_filter(filterType: session, sessions: {american: true}, timezone: "UTC")\ncond inSess = sess > 0' },
  { label: 'entry', text: 'entry long when bull and inSess' },
  { label: 'stop (ATR)', text: 'stop (stopType: atr_based, atrPeriod: 14, atrMultiplier: 1.5)' },
  { label: 'take (R:R)', text: 'take (tpType: risk_reward, riskRewardRatio: 2)' },
  { label: 'custom pattern', text: 'pattern myPat = tsl () {\n  param lookback: int 2..200 = 30\n  value: close - prev(highest(high, lookback), 1)\n  signal: close > prev(highest(high, lookback), 1)\n}' },
];

const ScriptView: React.FC<ScriptViewProps> = ({ blocks, onApply }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [diagnostics, setDiagnostics] = useState<ScriptDiagnostic[]>([]);
  const [compiledOk, setCompiledOk] = useState<boolean | null>(null);
  const [staleSource, setStaleSource] = useState(false);
  // View mode: 'full' (default — entire code incl. canonical bodies) or
  // 'compact' (canonical blocks folded to `use` references).
  const [viewMode, setViewMode] = useState<'full' | 'compact'>('full');
  const [cursor, setCursor] = useState({ line: 1, col: 1 });
  const [viewCanonical, setViewCanonical] = useState<string | null>(null);

  const taRef = useRef<HTMLTextAreaElement>(null);
  const hlRef = useRef<HTMLPreElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const printedRef = useRef('');

  const canonicalIds = useMemo(() => CANONICAL_BLOCKS.map(c => c.id), []);
  const canonicalDef = useMemo(
    () => (viewCanonical ? CANONICAL_BLOCKS.find(c => c.id === viewCanonical) ?? null : null),
    [viewCanonical],
  );
  const lineCount = useMemo(() => text.split('\n').length, [text]);
  const highlighted = useMemo(() => highlight(text) + '\n', [text]);

  const printFromGraph = useCallback((mode: 'full' | 'compact', sourceBlocks: Block[]) => {
    const printed = printGraph(
      sourceBlocks as unknown as Array<Record<string, unknown>>,
      mode === 'compact' ? { fold: true } : undefined,
    );
    printedRef.current = printed.text;
    setText(printed.text);
    setDiagnostics([]);
    setCompiledOk(null);
  }, []);

  const openView = useCallback(() => {
    try {
      // ALWAYS a fresh projection of the live graph — never stored text.
      printFromGraph('full', blocks);
      setViewMode('full');
      const cfg = blocks.find(b => b.type === 'strategy_config');
      const storedHash = (cfg?.metadata as Record<string, unknown> | undefined)?.sourceHash;
      setStaleSource(
        typeof storedHash === 'string' &&
        storedHash !== graphSemanticHash(blocks as unknown as Array<Record<string, unknown>>),
      );
      setOpen(true);
    } catch (e) {
      setText(`// printer error: ${e instanceof Error ? e.message : String(e)}`);
      setOpen(true);
    }
  }, [blocks, printFromGraph]);

  const switchMode = useCallback((mode: 'full' | 'compact') => {
    if (mode === viewMode) return;
    if (text !== printedRef.current &&
        !window.confirm('Switching views regenerates the script from the current graph — your unapplied edits will be discarded. Continue?')) {
      return;
    }
    setViewMode(mode);
    printFromGraph(mode, blocks);
  }, [viewMode, text, blocks, printFromGraph]);

  const compile = useCallback((apply: boolean) => {
    const result = compileScript(text);
    setDiagnostics(result.diagnostics);
    setCompiledOk(result.ok);
    if (result.ok && apply && result.blocks) {
      const hash = graphSemanticHash(result.blocks as unknown as Array<Record<string, unknown>>);
      const stamped = result.blocks.map(b =>
        b.type === 'strategy_config'
          ? { ...b, metadata: { ...(b.metadata ?? {}), sourceText: text, sourceHash: hash } }
          : b,
      );
      onApply(stamped as unknown as Block[]);
      setOpen(false);
    }
  }, [text, onApply]);

  const syncScroll = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    if (hlRef.current) {
      hlRef.current.scrollTop = ta.scrollTop;
      hlRef.current.scrollLeft = ta.scrollLeft;
    }
    if (gutterRef.current) gutterRef.current.scrollTop = ta.scrollTop;
  }, []);

  const updateCursor = useCallback(() => {
    const ta = taRef.current;
    if (!ta) return;
    const upto = ta.value.slice(0, ta.selectionStart);
    const line = (upto.match(/\n/g)?.length ?? 0) + 1;
    const col = upto.length - (upto.lastIndexOf('\n') + 1) + 1;
    setCursor({ line, col });
  }, []);

  const insertCanonical = useCallback((def: (typeof CANONICAL_BLOCKS)[number], asEditableCopy: boolean) => {
    const params = Object.entries(def.defaults ?? {})
      .map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(', ');
    const name = 'my' + def.id.replace(/_v\d+$/, '').replace(/(?:^|_)([a-z])/g, (_m, c: string) => c.toUpperCase());
    let line: string;
    if (asEditableCopy && def.tslText) {
      // TSL migration: per-bar canonicals inline their safe-TSL body (inner
      // statements only — the statement keyword supplies the header). Per-trade
      // canonicals have no inline body and fall through to `use` below.
      const head =
        def.blockType === 'pattern' ? `pattern ${name} =` :
        def.blockType === 'filter' ? `filter ${name} =` :
        `ind ${name} =`;
      const inner = def.tslText.split('\n').slice(1, -1).join('\n');
      line = `${head} tsl (${params}) {\n${inner}\n}`;
    } else {
      const head =
        def.blockType === 'pattern' ? `pattern ${name} = use` :
        def.blockType === 'filter' ? `filter ${name} = use` :
        def.blockType === 'qty_calculator' ? `calc ${name} = qty use` :
        def.blockType === 'risk_rule' ? 'risk use' : `ind ${name} = use`;
      line = `${head} ${def.id}(${params})`;
    }
    setText(t => t.trimEnd() + '\n' + line + '\n');
    setViewCanonical(null);
  }, []);

  if (!open) {
    return (
      <button
        onClick={openView}
        className="flex items-center px-3 py-2 text-sm bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors"
        title="View / edit this strategy as TSS — Tradeatory Strategy Script (compiles to the TSL graph the engine executes — the graph stays the source of truth)"
      >
        <Code2 className="w-4 h-4 mr-1.5" />
        TSS
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(false)}
        className="flex items-center px-3 py-2 text-sm bg-slate-700 text-white rounded-md"
      >
        <Code2 className="w-4 h-4 mr-1.5" />
        TSS
      </button>
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-5xl h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="flex items-center gap-2 min-w-0">
              <Code2 className="w-5 h-5 text-slate-700 shrink-0" />
              <h3 className="font-semibold text-gray-800 shrink-0">TSS — Tradeatory Strategy Script</h3>
              <div className="flex rounded-md border border-gray-300 overflow-hidden shrink-0 ml-2" role="tablist">
                <button
                  onClick={() => switchMode('full')}
                  className={`px-2.5 py-1 text-[11px] ${viewMode === 'full' ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  title="Entire logic — canonical block code shown inline. This is also what the AI sees."
                >
                  Full code
                </button>
                <button
                  onClick={() => switchMode('compact')}
                  className={`px-2.5 py-1 text-[11px] ${viewMode === 'compact' ? 'bg-slate-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  title="Canonical blocks folded to one-line use references (token-minimal form). Compiles to the same graph."
                >
                  Compact
                </button>
              </div>
              {staleSource && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                  <RefreshCw className="w-3 h-3" /> regenerated from graph
                </span>
              )}
              <span className="text-[11px] text-gray-500 truncate">
                generated from the current TSL graph — TSL remains the source of truth
              </span>
            </div>
            <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-600 shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] text-gray-500 mr-1">Insert:</span>
            {SNIPPETS.map(s => (
              <button
                key={s.label}
                onClick={() => setText(t => t.trimEnd() + '\n' + s.text + '\n')}
                className="text-[11px] px-2 py-0.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                {s.label}
              </button>
            ))}
            <span className="text-[11px] text-gray-500 ml-2 mr-1">Registry ({canonicalIds.length}):</span>
            {canonicalIds.map(id => (
              <button
                key={id}
                onClick={() => setViewCanonical(v => (v === id ? null : id))}
                className={`text-[10px] px-1.5 py-0.5 rounded border font-mono ${viewCanonical === id ? 'border-indigo-400 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >
                {id}
              </button>
            ))}
          </div>

          {canonicalDef && (
            <div className="border-b border-indigo-100 bg-indigo-50/50 px-4 py-2 max-h-56 overflow-y-auto shrink-0">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[11px] font-semibold text-indigo-800 font-mono">
                  {canonicalDef.id} <span className="font-sans font-normal text-indigo-500">({canonicalDef.blockType} — read-only, parity-validated) — {canonicalDef.description}</span>
                </span>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => insertCanonical(canonicalDef, false)}
                    className="text-[10px] px-2 py-0.5 rounded bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    insert use reference
                  </button>
                  <button
                    onClick={() => insertCanonical(canonicalDef, true)}
                    title="Pastes the full code as an inline ts block — it compiles to a NEW custom block you own and can edit. The canonical block itself is never modified."
                    className="text-[10px] px-2 py-0.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                  >
                    insert editable copy
                  </button>
                  <button onClick={() => setViewCanonical(null)} className="text-indigo-400 hover:text-indigo-600">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              <pre className={`${MONO} text-gray-700 whitespace-pre-wrap`}>{canonicalDef.tslText ?? `use ${canonicalDef.id}(…) — native engine implementation (no inline body)`}</pre>
            </div>
          )}

          {/* ── Editor: gutter + highlight overlay + transparent textarea ── */}
          <div className="flex-1 flex min-h-0 border-b border-gray-200">
            <div
              ref={gutterRef}
              className={`${MONO} shrink-0 w-12 overflow-hidden bg-gray-50 border-r border-gray-200 text-right text-gray-400 select-none py-3`}
              aria-hidden
            >
              {Array.from({ length: lineCount }, (_, i) => (
                <div key={i} className="px-2">{i + 1}</div>
              ))}
            </div>
            <div className="relative flex-1 min-w-0">
              <pre
                ref={hlRef}
                aria-hidden
                className={`${MONO} absolute inset-0 m-0 p-3 overflow-auto whitespace-pre pointer-events-none text-gray-800`}
                dangerouslySetInnerHTML={{ __html: highlighted }}
              />
              <textarea
                ref={taRef}
                value={text}
                onChange={(e) => { setText(e.target.value); setCompiledOk(null); }}
                onScroll={syncScroll}
                onKeyUp={updateCursor}
                onClick={updateCursor}
                onSelect={updateCursor}
                wrap="off"
                spellCheck={false}
                className={`${MONO} absolute inset-0 w-full h-full p-3 bg-transparent text-transparent caret-slate-800 whitespace-pre overflow-auto resize-none outline-none`}
              />
            </div>
          </div>

          {/* status bar */}
          <div className={`px-4 py-1 border-b border-gray-100 flex items-center justify-between text-[11px] text-gray-500 bg-gray-50`}>
            <span>Ln {cursor.line}, Col {cursor.col} · {lineCount} lines · {viewMode === 'full' ? 'full code' : 'compact (use refs)'} view</span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 text-gray-400" />
              TSS never executes — applying compiles it to TSL blocks; the engine runs TSL only.
            </span>
          </div>

          {diagnostics.length > 0 && (
            <div className="max-h-32 overflow-y-auto border-b border-red-100 bg-red-50 px-4 py-2 shrink-0">
              {diagnostics.slice(0, 12).map((d, i) => (
                <div key={i} className="text-[12px] text-red-700 font-mono">
                  line {d.line}:{d.col} — {d.message}
                  {d.expected ? ` (expected ${d.expected})` : ''}
                  {d.hint ? <span className="text-red-500"> · {d.hint}</span> : null}
                </div>
              ))}
            </div>
          )}
          {compiledOk === true && diagnostics.length === 0 && (
            <div className="border-b border-emerald-100 bg-emerald-50 px-4 py-1.5 text-[12px] text-emerald-700 flex items-center gap-1.5 shrink-0">
              <CheckCircle className="w-4 h-4" /> Compiles cleanly to a canonical TSL graph.
            </div>
          )}

          <div className="flex items-center justify-end gap-2 px-4 py-2.5 shrink-0">
            <button
              onClick={() => compile(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Check
            </button>
            <button
              onClick={() => compile(true)}
              className="flex items-center px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <Wand2 className="w-4 h-4 mr-1.5" />
              Compile &amp; apply to canvas
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ScriptView;
